import { CdkDialogContainer } from "@angular/cdk/dialog";
import { CdkPortalOutlet } from "@angular/cdk/portal";
import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, ViewEncapsulation } from "@angular/core";
import { VdsDrawerConfig } from "./drawer-config";

const OPENING_CLASS = "vds-drawer--enter";

const CLOSING_CLASS = "vds-drawer--leave";

/* Duration of the animations in milliseconds */
export const DRAWER_ANIMATION_DURATION = 400;

/** Event that captures the state of drawer container animations. */
interface DrawerAnimationEvent {
    state: "opened" | "opening" | "closing" | "closed";
    totalTime: number;
}

@Component({
    selector: "vds-drawer-container",
    templateUrl: "./drawer-container.html",
    styleUrl: "./drawer-container.css",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        class: "vds-drawer",
        tabindex: "-1",
        "[class.vds-drawer-left]": "_config.position === 'left'",
        "[class.vds-drawer-right]": "_config.position === 'right'",
        "[class.vds-drawer--no-scroll]": "_config.disableScroll",
        "[attr.aria-modal]": "_config.ariaModal",
        "[id]": "_config.id",
        "[attr.aria-labelledby]": "_config.ariaLabel ? null : _ariaLabelledByQueue[0]",
        "[attr.aria-label]": "_config.ariaLabel",
        "[attr.aria-describedby]": "_config.ariaDescribedBy || null"
    },
    imports: [CdkPortalOutlet]
})
export class VdsDrawerContainer extends CdkDialogContainer<VdsDrawerConfig> implements OnDestroy {
    private hostElement: HTMLElement = this._elementRef.nativeElement;

    openingClass = `${OPENING_CLASS}-${this._config.position}`;
    closingClass = `${CLOSING_CLASS}-${this._config.position}`;

    /** Emits when an animation state changes. */
    _animationStateChanged = new EventEmitter<DrawerAnimationEvent>();

    /** Current timer for drawer animations. */
    private _animationTimer: ReturnType<typeof setTimeout> | null = null;

    protected override _contentAttached() {
        // Delegate to the original dialog-container initialization (i.e. saving the
        // previous element, setting up the focus trap and moving focus to the container).
        super._contentAttached();

        this._startOpenAnimation();
    }

    private _startOpenAnimation() {
        this._animationStateChanged.emit({ state: "opening", totalTime: DRAWER_ANIMATION_DURATION });

        this._requestAnimationFrame(() => this.hostElement.classList.add(this.openingClass));
        this._waitForAnimationToComplete(DRAWER_ANIMATION_DURATION, this._finishDrawerOpen);
    }

    _startExitAnimation() {
        this._animationStateChanged.emit({ state: "closing", totalTime: DRAWER_ANIMATION_DURATION });
        this._requestAnimationFrame(() => this.hostElement.classList.add(this.closingClass));
        this._waitForAnimationToComplete(DRAWER_ANIMATION_DURATION, this._finishDrawerClose);
    }

    private _finishDrawerOpen = () => {
        this._clearAnimationClasses();
        this._openAnimationDone(DRAWER_ANIMATION_DURATION);
    };

    private _finishDrawerClose = () => {
        this._clearAnimationClasses();
        this._animationStateChanged.emit({ state: "closed", totalTime: DRAWER_ANIMATION_DURATION });
    };

    protected override _captureInitialFocus() {
        if (!this._config.delayFocusTrap) {
            this._trapFocus();
        }
    }

    private _openAnimationDone(totalTime: number) {
        if (this._config.delayFocusTrap) {
            this._trapFocus();
        }

        this._animationStateChanged.next({ state: "opened", totalTime });
    }

    /** Clears all drawer animation classes. */
    private _clearAnimationClasses() {
        this.hostElement.classList.remove(OPENING_CLASS, CLOSING_CLASS);
    }

    private _waitForAnimationToComplete(duration: number, callback: () => void) {
        if (this._animationTimer !== null) {
            clearTimeout(this._animationTimer);
        }

        // Note that we want this timer to run inside the NgZone, because we want
        // the related events like `afterClosed` to be inside the zone as well.
        this._animationTimer = setTimeout(callback, duration);
    }

    /** Runs a callback in `requestAnimationFrame`, if available. */
    private _requestAnimationFrame(callback: () => void) {
        this._ngZone.runOutsideAngular(() => {
            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(callback);
            } else {
                callback();
            }
        });
    }
}
